import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { Plus, Trash2, Printer, Pencil } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(n || 0);

const emptyForm = () => ({
  title: "Comprehensive treatment plan",
  diagnosis: "",
  notes: "",
  items: [{ description: "", qty: 1, unit_price: 0, gst_rate: 18 }],
});

export default function TreatmentPlans({ patientId, patientName }) {
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = creating new
  const [form, setForm] = useState(emptyForm());

  const load = () =>
    api.get("/treatment-plans", { params: { patient_id: patientId } }).then((r) => setPlans(r.data));
  useEffect(() => { load(); }, [patientId]);

  const setItem = (i, k, v) => setForm((f) => ({ ...f, items: f.items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { description: "", qty: 1, unit_price: 0, gst_rate: 18 }] }));
  const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const total = form.items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unit_price) || 0) * (1 + (Number(it.gst_rate) || 0) / 100), 0);
  const subtotal = form.items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unit_price) || 0), 0);
  const gstTotal = total - subtotal;

  const openNew = () => { setEditingId(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (plan) => {
    setEditingId(plan.id);
    setForm({
      title: plan.title,
      diagnosis: plan.diagnosis || "",
      notes: plan.notes || "",
      items: plan.items.length ? plan.items.map((it) => ({ ...it })) : [{ description: "", qty: 1, unit_price: 0, gst_rate: 18 }],
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const items = form.items.filter((it) => it.description).map((it) => ({
      description: it.description, qty: Number(it.qty), unit_price: Number(it.unit_price), gst_rate: Number(it.gst_rate),
    }));
    if (items.length === 0) return toast.error("Add at least one line item");
    const payload = { title: form.title, diagnosis: form.diagnosis, notes: form.notes, items };
    try {
      if (editingId) {
        await api.put(`/treatment-plans/${editingId}`, payload);
        toast.success("Treatment plan updated");
      } else {
        await api.post("/treatment-plans", { ...payload, patient_id: patientId });
        toast.success("Treatment plan saved");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const printPlan = (plan) => {
    const w = window.open("", "_blank");
    if (!w) return;
    const rows = plan.items.map((it) => `<tr>
        <td style="padding:4px 0;">${it.description}</td>
        <td>${it.qty}</td>
        <td style="text-align:right;">${fmt(it.unit_price)}</td>
        <td style="text-align:right;">${it.gst_rate}%</td>
        <td style="text-align:right;">${fmt(it.qty * it.unit_price * (1 + it.gst_rate / 100))}</td>
      </tr>`).join("");
    w.document.write(`<html><head><title>${plan.title}</title>
      <style>body{font-family:'IBM Plex Sans',sans-serif;padding:32px;color:#1C1C1C;} h1{font-family:'Manrope',sans-serif;margin:0 0 4px;} table{width:100%;margin-top:8px;border-collapse:collapse;font-size:12px;} th{padding:6px 0;text-align:left;color:#52525B;border-bottom:1px solid #E5E5E2;}</style></head>
      <body><div style="color:#E27B58;text-transform:uppercase;font-size:11px;letter-spacing:.15em;font-weight:600;">Treatment Plan</div>
      <h1>${plan.title}</h1>
      <div style="color:#52525B;margin-bottom:24px;">${patientName} · ${new Date(plan.created_at).toLocaleDateString("en-IN")}</div>
      ${plan.diagnosis ? `<div style="margin-bottom:16px;"><b>Diagnosis:</b> ${plan.diagnosis}</div>` : ""}
      <div style="border:1px solid #E5E5E2;border-radius:6px;padding:16px;">
        <table>
          <thead><tr><th>Item</th><th>Qty</th><th style="text-align:right;">Unit</th><th style="text-align:right;">GST</th><th style="text-align:right;">Line</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
        <div style="margin-top:8px;text-align:right;font-family:'IBM Plex Mono',monospace;font-weight:700;">Total: ${fmt(plan.total)}</div>
        ${plan.notes ? `<div style="margin-top:8px;font-size:12px;">${plan.notes}</div>` : ""}
      </div>
      <div style="margin-top:24px;font-size:11px;color:#A1A1AA;">DentOS · Dental Studio · This estimate is valid for 30 days.</div>
      </body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
  };

  const label = "text-xs font-semibold uppercase tracking-wider text-[#52525B] mb-1 block";
  const inp = "w-full bg-white border border-[#E5E5E2] text-sm px-3 py-2 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#0F4C3A]";

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-display text-lg font-bold tracking-tight">Treatment Plans</h2>
        <button data-testid="new-plan-btn" onClick={openNew} className="text-sm px-3 py-1.5 bg-[#0F4C3A] text-white rounded-md hover:bg-[#16654F] inline-flex items-center gap-1">
          <Plus className="w-4 h-4" /> New plan
        </button>
      </div>

      {plans.length === 0 && <div className="text-sm text-[#52525B]">No treatment plans yet.</div>}

      <div className="space-y-4">
        {plans.map((p) => (
          <div key={p.id} className="bg-white border border-[#E5E5E2] rounded-md p-5" data-testid={`plan-${p.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-display font-bold text-lg">{p.title}</div>
                <div className="text-xs text-[#52525B] font-mono-num">{new Date(p.created_at).toLocaleDateString("en-IN")}</div>
                {p.diagnosis && <div className="text-sm mt-1 text-[#52525B]">{p.diagnosis}</div>}
              </div>
              <div className="flex items-center gap-2">
                <div className="font-display font-bold text-lg font-mono-num">{fmt(p.total)}</div>
                {!p.invoice_id && (
                  <button onClick={() => openEdit(p)} data-testid={`edit-plan-${p.id}`} className="text-xs px-2 py-1 border border-[#E5E5E2] rounded-sm hover:bg-[#F4F4F5] inline-flex items-center gap-1">
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                )}
                {!p.invoice_id && (
                  <button onClick={async () => {
                    try {
                      const { data } = await api.post(`/treatment-plans/${p.id}/convert-to-invoice`);
                      toast.success(`Draft invoice ${data.invoice_no} created (${fmt(data.total)})`);
                      load();
                    } catch (e) { toast.error(formatApiError(e)); }
                  }} data-testid={`convert-invoice-${p.id}`} className="text-xs px-3 py-1 bg-[#E27B58] text-white rounded-sm hover:bg-[#c96843] inline-flex items-center gap-1 font-semibold">
                    Convert to Invoice →
                  </button>
                )}
                {p.invoice_id && (
                  <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-sm bg-[#F4F4F5] text-[#52525B]">Invoiced</span>
                )}
                <button onClick={() => printPlan(p)} className="text-xs px-2 py-1 border border-[#E5E5E2] rounded-sm hover:bg-[#F4F4F5] inline-flex items-center gap-1" data-testid={`print-plan-${p.id}`}>
                  <Printer className="w-3 h-3" /> Print
                </button>
              </div>
            </div>
            <ul className="text-sm space-y-1 text-[#52525B]">
              {p.items.map((it, i) => (
                <li key={i} className="flex justify-between">
                  <span>{it.description} <span className="text-xs">· qty {it.qty} · GST {it.gst_rate}%</span></span>
                  <span className="font-mono-num text-[#1C1C1C]">{fmt(it.qty * it.unit_price * (1 + it.gst_rate / 100))}</span>
                </li>
              ))}
            </ul>
            {p.notes && <div className="text-sm mt-2 text-[#52525B]">{p.notes}</div>}
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <form onSubmit={submit} className="w-full max-w-3xl bg-white rounded-md shadow-2xl max-h-[92vh] overflow-auto">
            <div className="px-6 py-4 border-b border-[#E5E5E2] flex justify-between items-center sticky top-0 bg-white z-10">
              <h3 className="font-display text-lg font-bold">{editingId ? "Edit treatment plan" : "New treatment plan"}</h3>
              <button type="button" onClick={() => setOpen(false)} className="text-sm text-[#52525B]">Close</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><label className={label}>Title *</label><input required className={inp} value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="plan-title" /></div>
                <div><label className={label}>Diagnosis</label><input className={inp} value={form.diagnosis} onChange={(e) => setForm((f) => ({ ...f, diagnosis: e.target.value }))} /></div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <div className={label + " mb-0"}>Line items</div>
                  <button type="button" onClick={addItem} className="text-xs px-2 py-1 border border-[#E5E5E2] rounded-sm hover:bg-[#F4F4F5] inline-flex items-center gap-1" data-testid="add-item-btn"><Plus className="w-3 h-3" /> Add</button>
                </div>
                <div className="space-y-2">
                  {form.items.map((it, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                      <input placeholder="Procedure/item" className={`${inp} col-span-5`} value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} data-testid={`plan-item-${i}-desc`} />
                      <input type="number" min="1" placeholder="Qty" className={`${inp} col-span-1`} value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} />
                      <input type="number" min="0" placeholder="Unit ₹" className={`${inp} col-span-2`} value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} data-testid={`plan-item-${i}-price`} />
                      <input type="number" min="0" step="0.1" placeholder="GST %" className={`${inp} col-span-2`} value={it.gst_rate} onChange={(e) => setItem(i, "gst_rate", e.target.value)} />
                      <div className="col-span-1 text-right text-xs font-mono-num text-[#52525B]">
                        {fmt((Number(it.qty) || 0) * (Number(it.unit_price) || 0))}
                      </div>
                      <button type="button" onClick={() => removeItem(i)} className="col-span-1 text-[#D93838] hover:bg-[#FEE2E2] rounded-sm p-2 flex justify-center"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className={label}>Notes</label>
                <textarea rows={3} className={inp} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
              </div>

              <div className="bg-[#FDFDFB] border border-[#E5E5E2] rounded-md p-4 grid grid-cols-3 gap-2 text-sm font-mono-num">
                <div><div className="text-xs uppercase tracking-wider text-[#52525B]">Subtotal</div>{fmt(subtotal)}</div>
                <div><div className="text-xs uppercase tracking-wider text-[#52525B]">GST</div>{fmt(gstTotal)}</div>
                <div><div className="text-xs uppercase tracking-wider text-[#52525B]">Total</div><span className="font-bold text-base text-[#0F4C3A]">{fmt(total)}</span></div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[#E5E5E2] flex justify-end gap-2 sticky bottom-0 bg-white">
              <button type="button" onClick={() => setOpen(false)} className="text-sm px-4 py-2 border border-[#E5E5E2] rounded-md">Cancel</button>
              <button type="submit" data-testid="save-plan-btn" className="text-sm px-4 py-2 bg-[#0F4C3A] text-white rounded-md hover:bg-[#16654F]">{editingId ? "Save changes" : "Save plan"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
